import { Pressable } from 'react-native';
import Animated from 'react-native-reanimated';

import { Box, HStack, Text, VStack, theme } from '@squire/ui';

import { GiftIcon } from '@/components/_icons';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { formatPrice } from '@/utils/internationalizationUtils';

import { styles } from './styles';
import type { ServiceCardGiftProps } from './types';

/** Matches smallest preset on the gift card amount screen (see GiftCardAmountScreen PRESET_AMOUNTS_CENTS, divided by 100 for display). */
const GIFT_CARD_LIST_MIN_AMOUNT = 50;

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

function ServiceCardGift({
  onPress,
  testID,
  style,
  currency = 'USD',
  minimumAmount = GIFT_CARD_LIST_MIN_AMOUNT,
  ...pressableRest
}: ServiceCardGiftProps) {
  const { t, i18n } = useScreenTranslations('ServiceCard');

  const formattedMin = formatPrice(minimumAmount, {
    locale: i18n.language,
    currency,
  });
  const fromPriceText = t('giftCard.fromPrice', { price: formattedMin });

  return (
    <AnimatedPressable
      {...pressableRest}
      onPress={() => onPress?.()}
      style={[styles.container]}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={t('giftCard.accessibility.label', {
        fromPrice: fromPriceText,
      })}
      accessibilityHint={t('giftCard.accessibility.hint')}
    >
      {({ pressed }) => (
        <Box style={[styles.content({ pressed }), style]}>
          <VStack space={theme.spacing[4]}>
            <HStack space={theme.spacing[3]} centerVertical>
              <GiftIcon size={23} />
              <Text variant="mStrong" color="textPrimary" numberOfLines={4}>
                {t('giftCard.title')}
              </Text>
            </HStack>
          </VStack>
          <Box style={styles.price}>
            <Text variant="sStrong">{fromPriceText}</Text>
          </Box>
        </Box>
      )}
    </AnimatedPressable>
  );
}

ServiceCardGift.displayName = 'ServiceCardGift';
export { ServiceCardGift };
